import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'https://api.faronexo.com').replace(/\/$/, '');
const API_URL = `${API_BASE_URL}/v1/public/assessment-requests`;
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAEvZv_7Y1KV9OLWi';
const TURNSTILE_TIMEOUT_MS = 10000;
let turnstileLoaderPromise;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoaderPromise) return turnstileLoaderPromise;

  turnstileLoaderPromise = new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const waitUntilReady = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      if (Date.now() - startedAt >= TURNSTILE_TIMEOUT_MS) {
        reject(new Error('turnstile_load_timeout'));
        return;
      }
      window.setTimeout(waitUntilReady, 100);
    };

    waitUntilReady();
  });

  return turnstileLoaderPromise;
}
const EMPTY_FORM = {
  nombre: '', apellido: '', empresa: '', cargo: '', email: '', sector: '',
  web_dominio: '', url_api: '', asunto: '', detalle: '', activos: '',
  fallas: '', servicio: '', plazo: '', consentimiento: false,
};

const FLOWS = {
  'Diagnóstico inicial': {
    intro: 'Compartí el contexto, los activos que te preocupan y cualquier falla observada. Lo revisaremos desde la consola antes de contactarte.',
    button: 'Solicitar diagnóstico',
  },
  'Escaneo de seguridad': {
    intro: 'Indicá el dominio y, si corresponde, la API a evaluar. La solicitud quedará pendiente en la consola y ningún escaneo comenzará sin aprobación.',
    button: 'Solicitar escaneo',
  },
  'Consulta general': {
    intro: 'Escribí tu consulta. La registraremos y el equipo recibirá una notificación para responderte por correo.',
    button: 'Enviar consulta',
  },
  'Solicitud de servicio': {
    intro: 'Elegí el servicio que necesitás y contanos el objetivo. Te responderemos con los próximos pasos.',
    button: 'Solicitar servicio',
  },
};

function AssessmentForm() {
  const { toast } = useToast();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileStatus, setTurnstileStatus] = useState('loading');
  const turnstileContainerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const flow = FLOWS[formData.asunto];

  useEffect(() => {
    if (!flow) return undefined;

    let cancelled = false;
    setTurnstileStatus('loading');

    loadTurnstile().then((turnstile) => {
      if (cancelled || !turnstileContainerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: 'assessment_request',
        theme: 'dark',
        appearance: 'always',
        callback: (token) => {
          setTurnstileToken(token);
          setTurnstileStatus('verified');
        },
        'expired-callback': () => {
          setTurnstileToken('');
          setTurnstileStatus('ready');
        },
        'error-callback': () => {
          setTurnstileToken('');
          setTurnstileStatus('error');
        },
      });
      setTurnstileStatus('ready');
    }).catch(() => {
      if (!cancelled) setTurnstileStatus('error');
    });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [flow]);

  const description = useMemo(() => {
    const parts = [];
    if (formData.asunto === 'Diagnóstico inicial') {
      parts.push(`Activos o entornos informados:\n${formData.activos}`);
      if (formData.fallas) parts.push(`Fallas o señales observadas:\n${formData.fallas}`);
    }
    if (formData.asunto === 'Solicitud de servicio') {
      parts.push(`Servicio de interés: ${formData.servicio}`);
      if (formData.plazo) parts.push(`Plazo estimado: ${formData.plazo}`);
    }
    parts.push(`Detalle:\n${formData.detalle}`);
    return parts.join('\n\n');
  }, [formData]);

  const isFormComplete = useMemo(() => {
    if (!flow) return false;

    const baseFields = ['nombre', 'apellido', 'empresa', 'cargo', 'email', 'sector', 'web_dominio', 'detalle'];
    if (!baseFields.every((field) => formData[field].trim()) || !formData.consentimiento) return false;
    if (formData.asunto === 'Diagnóstico inicial' && !formData.activos.trim()) return false;
    if (formData.asunto === 'Solicitud de servicio' && !formData.servicio) return false;
    return true;
  }, [flow, formData]);

  const handleChange = ({ target }) => setFormData((previous) => ({
    ...previous, [target.name]: target.type === 'checkbox' ? target.checked : target.value,
  }));

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (window.turnstile && widgetIdRef.current !== null) window.turnstile.reset(widgetIdRef.current);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!turnstileToken) {
      toast({ title: 'Verificación pendiente', description: 'Completá la verificación de seguridad antes de enviar.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(API_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.nombre, last_name: formData.apellido, company: formData.empresa,
          job_title: formData.cargo, email: formData.email, industry: formData.sector,
          primary_domain: formData.web_dominio, api_url: formData.url_api,
          subject: formData.asunto, description, consent: formData.consentimiento,
          turnstile_token: turnstileToken,
        }),
      });
      if (!response.ok) {
        if (response.status === 409) throw new Error('duplicate');
        if (response.status === 400) throw new Error('invalid');
        if (response.status === 403) throw new Error('verification');
        throw new Error('unavailable');
      }
      setFormData(EMPTY_FORM);
      toast({ title: 'Solicitud enviada', description: 'La recibimos correctamente y quedó pendiente de revisión. Te contactaremos a la brevedad.' });
    } catch (error) {
      const messages = {
        duplicate: 'Ya existe una solicitud activa para este correo y dominio.',
        invalid: 'Revisá los datos ingresados y volvé a intentar.',
        verification: 'No pudimos validar la verificación de seguridad. Intentá nuevamente.',
        unavailable: 'El servicio no está disponible. Intentá nuevamente en unos minutos.',
      };
      toast({ title: 'No pudimos enviar la solicitud', description: messages[error.message] || messages.unavailable, variant: 'destructive' });
    } finally { resetTurnstile(); setIsSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-card p-6 md:p-8 rounded-xl border border-border shadow-sm">
      <div>
        <h2 className="text-2xl font-semibold text-foreground mb-2">Solicitar evaluación</h2>
        <p className="text-muted-foreground text-sm">Elegí el motivo de contacto y completá únicamente la información necesaria.</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="asunto">¿Qué necesitás? *</Label>
        <Select value={formData.asunto} onValueChange={(asunto) => setFormData((previous) => ({ ...previous, asunto, detalle: '', activos: '', fallas: '', servicio: '', plazo: '' }))} required>
          <SelectTrigger id="asunto" className="bg-background text-foreground"><SelectValue placeholder="Seleccioná una opción" /></SelectTrigger>
          <SelectContent>
            {Object.keys(FLOWS).map((subject) => <SelectItem key={subject} value={subject}>{subject}</SelectItem>)}
          </SelectContent>
        </Select>
        {flow && <p className="text-sm text-muted-foreground pt-1">{flow.intro}</p>}
      </div>

      {flow && <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field id="nombre" label="Nombre *" value={formData.nombre} onChange={handleChange} maxLength={100} autoComplete="given-name" />
          <Field id="apellido" label="Apellido *" value={formData.apellido} onChange={handleChange} maxLength={100} autoComplete="family-name" />
          <Field id="empresa" label="Empresa *" value={formData.empresa} onChange={handleChange} maxLength={200} autoComplete="organization" />
          <Field id="cargo" label="Cargo *" value={formData.cargo} onChange={handleChange} maxLength={200} autoComplete="organization-title" />
          <Field id="email" label="Email corporativo *" type="email" value={formData.email} onChange={handleChange} maxLength={320} autoComplete="email" />
          <Field id="sector" label="Sector / Industria *" value={formData.sector} onChange={handleChange} maxLength={200} />
        </div>

        {(formData.asunto === 'Diagnóstico inicial' || formData.asunto === 'Escaneo de seguridad') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field id="web_dominio" label="Web o dominio principal *" value={formData.web_dominio} onChange={handleChange} maxLength={253} placeholder="ejemplo.com" autoCapitalize="none" spellCheck={false} />
            <Field id="url_api" label="URL / API a revisar (opcional)" type="url" required={false} value={formData.url_api} onChange={handleChange} maxLength={2048} placeholder="https://api.ejemplo.com" autoCapitalize="none" spellCheck={false} />
          </div>
        )}
        {(formData.asunto === 'Consulta general' || formData.asunto === 'Solicitud de servicio') && (
          <Field id="web_dominio" label="Web o dominio de la empresa *" value={formData.web_dominio} onChange={handleChange} maxLength={253} placeholder="ejemplo.com" autoCapitalize="none" spellCheck={false} />
        )}

        {formData.asunto === 'Diagnóstico inicial' && <>
          <Area id="activos" label="Activos o entornos a evaluar *" value={formData.activos} onChange={handleChange} placeholder="Dominios, aplicaciones, nube, sedes, equipos o servicios relevantes." />
          <Area id="fallas" label="Fallas o señales de seguridad observadas" required={false} value={formData.fallas} onChange={handleChange} placeholder="Alertas, accesos extraños, configuraciones débiles o incidentes conocidos." />
        </>}

        {formData.asunto === 'Solicitud de servicio' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="servicio">Servicio de interés *</Label>
              <Select value={formData.servicio} onValueChange={(servicio) => setFormData((previous) => ({ ...previous, servicio }))} required>
                <SelectTrigger id="servicio" className="bg-background text-foreground"><SelectValue placeholder="Seleccioná un servicio" /></SelectTrigger>
                <SelectContent>
                  {['Auditoría integral','Pentesting','Respuesta a incidentes','Implementación de herramientas','Arquitectura y consultoría','Concientización y políticas','Gestión de riesgos / vCISO'].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field id="plazo" label="¿Cuándo lo necesitás? (opcional)" required={false} value={formData.plazo} onChange={handleChange} maxLength={100} placeholder="Ej.: este mes, este trimestre" />
          </div>
        )}

        <Area id="detalle" label={formData.asunto === 'Consulta general' ? 'Consulta *' : 'Objetivo o detalle adicional *'} value={formData.detalle} onChange={handleChange} placeholder="Incluí el contexto necesario para que podamos orientarte." />

        <div className="flex items-start gap-3">
          <input id="consentimiento" name="consentimiento" type="checkbox" checked={formData.consentimiento} onChange={handleChange} required className="mt-1 h-4 w-4 rounded border-border accent-primary" />
          <Label htmlFor="consentimiento" className="text-sm leading-5 font-normal">Autorizo a Faro Nexo a usar estos datos para analizar mi solicitud y contactarme. *</Label>
        </div>
        <div className="min-h-[82px] space-y-2" aria-label="Verificación de seguridad">
          <Label>Verificación de seguridad *</Label>
          <div ref={turnstileContainerRef} />
          {turnstileStatus === 'loading' && <p className="text-sm text-muted-foreground">Cargando verificación de Cloudflare…</p>}
          {turnstileStatus === 'verified' && <p className="text-sm text-emerald-400">Verificación completada.</p>}
          {turnstileStatus === 'error' && <p className="text-sm text-destructive">No se pudo cargar la verificación. Recargá la página para volver a intentarlo.</p>}
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isSubmitting || !isFormComplete || !turnstileToken}>
          {isSubmitting ? 'Enviando...' : flow.button}
        </Button>
      </>}
    </form>
  );
}
function Field({ id, label, required = true, ...inputProps }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label><Input id={id} name={id} required={required} className="bg-background text-foreground" {...inputProps} /></div>;
}
function Area({ id, label, required = true, ...props }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label><Textarea id={id} name={id} required={required} maxLength={1200} rows={4} className="bg-background text-foreground resize-none" {...props} /></div>;
}
export default AssessmentForm;
